import type { IconName } from "@/components/icons/Icon";
import type {
  SingleSuggestion,
  SlashCommandType,
  SuggestionItem,
} from "@/components/suggestions/types";
import type { SuggestionIntlName } from "@/models/nodes/types";

export type SlashCommandItem = {
  kind: SlashCommandType;
  intlName: SuggestionIntlName;
  label: string;
  subtitle?: string;
  icon: IconName;
  hint?: string;
};

export type SlashCommandSuggestionItem = SuggestionItem<"slashCommand"> & {
  kind: SlashCommandType;
};

export function isSlashCommandSuggestion(
  suggestion: SingleSuggestion,
): suggestion is SingleSuggestion<"slashCommand"> & { item: SlashCommandSuggestionItem } {
  return suggestion.item.type === "slashCommand";
}

export const SLASH_COMMAND_ITEMS: SlashCommandItem[] = [
  {
    kind: "text",
    intlName: "slashCommand.text",
    label: "Text",
    icon: "block-format/Text",
  },
  {
    kind: "heading1",
    intlName: "slashCommand.heading1",
    label: "Title",
    subtitle: "Heading 1",
    icon: "block-format/Heading1",
  },
  {
    kind: "heading2",
    intlName: "slashCommand.heading2",
    label: "Section title",
    subtitle: "Heading 2",
    icon: "block-format/Heading2",
  },
  {
    kind: "heading3",
    intlName: "slashCommand.heading3",
    label: "Subsection title",
    subtitle: "Heading 3",
    icon: "block-format/Heading3",
  },
  {
    kind: "blockquote",
    intlName: "slashCommand.blockquote",
    label: "Quote",
    icon: "block-format/Blockquote",
  },
  {
    kind: "bulletList",
    intlName: "slashCommand.bulletList",
    label: "Bullet List",
    icon: "block-format/Bullet",
  },
  {
    kind: "orderedList",
    intlName: "slashCommand.orderedList",
    label: "Numbered List",
    icon: "block-format/Number",
  },
  {
    kind: "taskList",
    intlName: "slashCommand.taskList",
    label: "Task List",
    icon: "block-format/Task",
  },
  {
    kind: "callout",
    intlName: "slashCommand.callout",
    label: "Callout",
    icon: "block-format/Callout",
  },
  {
    kind: "code",
    intlName: "slashCommand.code",
    label: "Code",
    hint: "CC",
    icon: "block-format/Code",
  },
  {
    kind: "table",
    intlName: "slashCommand.table",
    label: "Table",
    icon: "block-format/Table",
  },
  {
    kind: "horizontalRule",
    intlName: "slashCommand.horizontalRule",
    label: "Separation line",
    icon: "block-format/Line",
  },
  {
    kind: "alignLeft",
    intlName: "slashCommand.alignLeft",
    label: "Align left",
    icon: "AlignTextLeft",
  },
  {
    kind: "alignCenter",
    intlName: "slashCommand.alignCenter",
    label: "Align center",
    icon: "AlignTextCenter",
  },
  {
    kind: "alignRight",
    intlName: "slashCommand.alignRight",
    label: "Align right",
    icon: "AlignTextRight",
  },
];

export function filterSlashCommandItems(query: string): SlashCommandItem[] {
  if (!query) return SLASH_COMMAND_ITEMS;
  const q = query.toLowerCase();
  return SLASH_COMMAND_ITEMS.filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.subtitle?.toLowerCase().includes(q) ||
      item.kind.toLowerCase().includes(q),
  );
}
